/**
 * Global Year Store
 * 
 * Zustand store for managing the global year filter across the application.
 * Persists to localStorage and loads from it on mount to prevent hydration mismatches.
 */
import React from 'react';
import { create } from 'zustand';

const STORAGE_KEY = 'reactor_year_filter';

/**
 * Get the initial year value.
 * Defaults to current year. localStorage is synced later in useEffect to avoid hydration mismatches.
 */
function getInitialYear(): number {
  return new Date().getFullYear();
}

interface YearState {
  year: number;
  setYear: (year: number) => void;
  isHydrated: boolean;
  setIsHydrated: (value: boolean) => void;
}

export const useYearStore = create<YearState>((set) => ({
  year: getInitialYear(),
  isHydrated: false,
  setYear: (year: number) => {
    // Validate year is reasonable
    if (isNaN(year) || year < 2000 || year > 2100) {
      console.warn('[YearStore] Invalid year value:', year);
      return;
    }

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, String(year));
    } catch (e) {
      // localStorage not available
    }

    set({ year });
  },
  setIsHydrated: (value: boolean) => {
    set({ isHydrated: value });
  },
}));

/**
 * Hook to hydrate year from localStorage on mount.
 * Call this once in a top-level component to prevent hydration mismatches.
 * 
 * IMPORTANT: Only loads from localStorage if it's the current year or within reasonable range.
 * This prevents stale data from blocking new campaigns.
 */
export function useHydrateYearFromStorage(): void {
  const setYear = useYearStore((state) => state.setYear);
  const setIsHydrated = useYearStore((state) => state.setIsHydrated);
  const isHydrated = useYearStore((state) => state.isHydrated);

  React.useEffect(() => {
    if (isHydrated) return; // Already hydrated

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        const currentYear = new Date().getFullYear();

        // Only use stored value if it's recent (within 1 year of current year)
        // This prevents old cached data from blocking new campaigns
        if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100 && Math.abs(parsed - currentYear) <= 1) {
          setYear(parsed);
        } else {
          // Clear invalid or stale localStorage data
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (e) {
            // Ignore
          }
        }
      }
    } catch (e) {
      // localStorage not available
    }

    setIsHydrated(true);
  }, [isHydrated, setYear, setIsHydrated]);
}

/**
 * Hook to get the current year value.
 */
export function useYear(): number {
  return useYearStore((state) => state.year);
}

/**
 * Hook to get the year setter function.
 */
export function useSetYear(): (year: number) => void {
  return useYearStore((state) => state.setYear);
}



